'use client';

import { useTranslations } from 'next-intl';
import { useId } from 'react';

import { Switch } from '@/components/ui/switch';
import { useHideFeaturedSlotControl } from '@/hooks/useHideFeaturedSlot';

/**
 * Toggle for the user-controlled opt-out of the Profil+ featured slot
 * (issue #3687, S1 soft requirement of #3663).
 *
 * Rendered on the ranking-disclosure page (/sk/zoradenie | /en/how-we-rank)
 * — its sole mount point and the compliance anchor / "single predictable
 * place to flip the preference back on" after hiding via the one-click X on
 * the slot in chat-match results. (The directory dropped its featured-slot
 * render in #5256, so this control no longer mounts there.) Registered users'
 * profile preferences expose the same opt-out via their own row layout, built
 * on the same `useHideFeaturedSlot` hook — the single source of truth.
 *
 * Persisted in localStorage and, for signed-in users, server-side
 * (`users.hide_featured_slot`, #5300) so it syncs across devices. The help
 * note reflects which: "synced to your account preferences" when signed in,
 * "saved in this browser" otherwise — the auth-gated honest copy of #5299
 * (anonymous visitors never see a reference to the auth-only preferences
 * page).
 *
 * Built on the Radix-based Switch primitive — `role="switch"` and
 * `aria-checked` are provided natively by Radix, so we only pass
 * `aria-labelledby` / `aria-describedby` for the visible label and
 * help text. Screen readers receive the on/off state explicitly.
 */
export function HideFeaturedSlotToggle() {
  const t = useTranslations('HowWeRank.control');
  const { hidden, setHidden, isSynced } = useHideFeaturedSlotControl();
  const labelId = useId();
  const descId = useId();

  return (
    <div className='mt-4 flex flex-col items-start gap-2'>
      <div className='flex items-center gap-3'>
        {/* Must stay interactive: this is the user-facing opt-in/opt-out the
            108/2024 disclosure promises ("one click away"). A `disabled` prop
            crept in via #5256 and silently froze the switch on /zoradenie (its
            sole render site) — HideFeaturedSlotToggle.test.tsx now fences it. */}
        <Switch
          aria-labelledby={labelId}
          aria-describedby={descId}
          checked={hidden}
          onCheckedChange={setHidden}
        />
        <span id={labelId} className='text-sm font-medium text-content-primary'>
          {hidden ? t('toggleHiddenLabel') : t('toggleVisibleLabel')}
        </span>
      </div>
      <p id={descId} className='text-xs text-content-muted'>
        {isSynced ? t('toggleNoteSynced') : t('toggleNote')}
      </p>
    </div>
  );
}
